import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {pluralize} from 'BenchlingUtil';
import {getMaxMultimerComponents} from 'model-hub/scientific-models/ScientificModelConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {GRAY_LIGHTER, GRAY_SEMIDARK, RED_LIGHT} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export interface SelectedComponentProps {
  componentId: string;
  count: number;
  isPrimary: boolean;
  disabled: boolean;
  incrementDisabled: boolean;
  model: string;
  onIncrement: (componentId: string) => void;
  onDecrement: (componentId: string) => void;
  onRemove: (componentId: string) => void;
}

export default function SelectedComponent({
  componentId,
  count,
  isPrimary,
  disabled,
  incrementDisabled,
  model,
  onIncrement,
  onDecrement,
  onRemove,
}: SelectedComponentProps): JSX.Element {
  const [isTrashHovered, trashHoverProps] = useHoverState();

  return (
    <div className={css(styles.componentRow)}>
      <div className={css(styles.componentInfo)}>
        <BenchChip id={componentId} size={ChipSize.SMALL} isLink={false} />
      </div>
      <div className={css(styles.componentControls)}>
        <div className={css(styles.counterControls)}>
          <Button
            size={ButtonSize.MINI}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            text="-"
            onClick={() => {
              onDecrement(componentId);
            }}
            disabled={disabled || (isPrimary && count <= 1)}
          />
          <span className={css(styles.countDisplay)}>{count}</span>
          <Tooltip
            text={`Maximum of ${pluralize(getMaxMultimerComponents(model), 'component')} allowed`}
            isDisabled={!incrementDisabled}
          >
            <Button
              size={ButtonSize.MINI}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              text="+"
              onClick={() => {
                onIncrement(componentId);
              }}
              disabled={disabled || incrementDisabled}
            />
          </Tooltip>
        </div>
        <div className={css(styles.actionControl)}>
          {!isPrimary && (
            <div {...trashHoverProps}>
              <Button
                size={ButtonSize.MINI}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                onClick={() => {
                  onRemove(componentId);
                }}
                disabled={disabled}
                className={css(isTrashHovered && !disabled ? styles.removeButtonHovered : undefined)}
              >
                <BenchlingIcon icon="trash-can" />
              </Button>
            </div>
          )}
          {isPrimary && <span className={css(styles.primaryProteinLabel)}>Primary</span>}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  componentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${GRAY_LIGHTER}`,
  },
  componentInfo: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
  },
  componentControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    minWidth: '200px',
  },
  counterControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginRight: '16px',
  },
  countDisplay: {
    ...FONT_PROXIMA_13,
    fontWeight: 600,
    minWidth: '20px',
    textAlign: 'center',
  },
  actionControl: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '70px',
    textAlign: 'center',
  },
  primaryProteinLabel: {
    ...FONT_PROXIMA_13,
    fontSize: '12px',
    color: GRAY_SEMIDARK,
    fontStyle: 'italic',
  },
  removeButtonHovered: {
    backgroundColor: RED_LIGHT,
    borderColor: RED_LIGHT,
    color: 'white',
    svg: {
      fill: 'white',
    },
  },
});
